import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, XCircle, Clock, Inbox } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { slotRows } from "@/constants/paymentMethods";

const STATUS_STYLES = {
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  rejected: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  superseded: "border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-4)]",
};

// Owner-only moderation queue for worker/staff payout methods. The owner inspects the exact
// submitted configuration, then approves (activates it) or rejects it. Pending/rejected
// methods are never usable payout destinations. Historical payment snapshots never change.
export const PayoutApprovals = () => {
  const [items, setItems] = useState(null);
  const [showDecided, setShowDecided] = useState(false);
  const [methods, setMethods] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // request id with the note box open
  const [rejectNote, setRejectNote] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/payout-approvals?status=all`, { headers: authHeaders() })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Could not load payout method requests."));
    axios.get(`${API}/payment-methods/meta`, { headers: authHeaders() })
      .then((r) => setMethods(r.data.methods || {}))
      .catch(() => {});
  }, []);
  useEffect(load, [load]);

  const decide = async (req, action) => {
    setBusyId(req.id);
    try {
      if (action === "approve") {
        await axios.post(`${API}/admin/payout-approvals/${req.id}/approve`, {}, { headers: authHeaders() });
        toast.success(`${req.method_label} approved for ${req.worker_name} — now the active ${req.slot} method.`);
      } else {
        await axios.post(`${API}/admin/payout-approvals/${req.id}/reject`, { note: rejectNote || undefined }, { headers: authHeaders() });
        toast.success(`Rejected — ${req.worker_name} was told to submit another method.`);
        setRejecting(null);
        setRejectNote("");
      }
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const pending = (items || []).filter((r) => r.status === "pending");
  const decided = (items || []).filter((r) => r.status === "approved" || r.status === "rejected");

  const Row = ({ r }) => (
    <div data-testid={`payout-approval-${r.id}`} className="rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-[var(--pv-text)]">
            {r.worker_name}
            {r.worker_role === "manager" && <span className="ml-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[9px] font-bold uppercase text-violet-300">Staff</span>}
          </p>
          <p className="mt-0.5 text-[11px] font-medium text-[var(--pv-text-4)]">
            {r.method_label} · Requested as {r.slot} · {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            {r.replaces ? " · replaces an approved method" : ""}
          </p>
        </div>
        <span data-testid={`payout-approval-status-${r.id}`} className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[r.status]}`}>
          {r.status === "pending" ? "Pending approval" : r.status}
        </span>
      </div>
      {/* the exact submitted configuration — inspect before deciding */}
      <dl data-testid={`payout-approval-fields-${r.id}`} className="mt-2.5 space-y-1 rounded-xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-3">
        {slotRows({ method: r.method, fields: r.fields }, methods).map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3 text-xs">
            <dt className="shrink-0 font-semibold text-[var(--pv-text-4)]">{row.label}</dt>
            <dd className="break-all text-right font-medium text-[var(--pv-text-2)]">{row.value || <span className="italic text-[var(--pv-text-4)]">—</span>}</dd>
          </div>
        ))}
      </dl>
      {r.status === "pending" && r.current_active && (
        <p className="mt-2 text-[10px] font-medium text-[var(--pv-text-4)]">
          Currently active {r.slot}: {r.current_active.method && methods[r.current_active.method]?.label} · {slotRows(r.current_active, methods).filter((x) => x.value).map((x) => x.value).join(" · ") || "on file"} — stays active unless you approve this change.
        </p>
      )}
      {r.status === "pending" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            data-testid={`payout-approve-${r.id}`}
            onClick={() => decide(r, "approve")}
            disabled={busyId === r.id}
            className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-60"
          >
            {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Approve
          </button>
          {rejecting === r.id ? (
            <span className="flex flex-1 flex-wrap items-center gap-2">
              <input
                data-testid={`payout-reject-note-${r.id}`}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="Reason (optional, shared with them)"
                className="min-w-40 flex-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3.5 py-2 text-xs font-medium text-[var(--pv-text)] focus:border-rose-400 focus:outline-none"
              />
              <button data-testid={`payout-reject-confirm-${r.id}`} onClick={() => decide(r, "reject")} disabled={busyId === r.id} className="rounded-full bg-rose-500 px-4 py-2 text-xs font-bold text-white hover:bg-rose-600 disabled:opacity-60">Confirm reject</button>
              <button onClick={() => { setRejecting(null); setRejectNote(""); }} className="text-xs font-bold text-[var(--pv-text-4)]">Cancel</button>
            </span>
          ) : (
            <button
              data-testid={`payout-reject-${r.id}`}
              onClick={() => setRejecting(r.id)}
              disabled={busyId === r.id}
              className="flex items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs font-bold text-rose-300 transition-colors hover:bg-rose-500/20 disabled:opacity-60"
            >
              <XCircle className="h-3.5 w-3.5" /> Reject
            </button>
          )}
        </div>
      )}
      {r.status === "rejected" && r.decision_note && (
        <p className="mt-2 text-[10px] font-medium text-rose-300/80">Reason shared: {r.decision_note}</p>
      )}
    </div>
  );

  return (
    <section data-testid="payout-approvals" className="mt-10 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[var(--pv-text)]">
          <ShieldCheck className="h-5 w-5 text-cyan-400" /> Payment method requests
          {pending.length > 0 && (
            <span data-testid="payout-approvals-count" className="rounded-full bg-amber-500/15 border border-amber-500/30 px-2.5 py-0.5 text-[11px] font-bold text-amber-300">{pending.length} pending</span>
          )}
        </h2>
        <button
          data-testid="payout-approvals-toggle-decided"
          onClick={() => setShowDecided((s) => !s)}
          className="text-[11px] font-bold text-[var(--pv-text-4)] transition-colors hover:text-cyan-400"
        >
          {showDecided ? "Hide history" : `Show decided (${decided.length})`}
        </button>
      </div>
      <p className="mt-1 text-xs text-[var(--pv-text-4)]">
        Workers and staff submit payout methods here for review. Nothing becomes a usable payout destination until you approve it.
      </p>
      {!items ? (
        <p className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin text-cyan-400" /></p>
      ) : pending.length === 0 ? (
        <p data-testid="payout-approvals-empty" className="mt-4 flex items-center gap-2 rounded-2xl border border-dashed border-[var(--pv-border-strong)] py-6 justify-center text-xs text-[var(--pv-text-4)]">
          <Inbox className="h-4 w-4" /> No pending requests — every payout method is decided.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {pending.map((r) => <Row key={r.id} r={r} />)}
        </div>
      )}
      {showDecided && decided.length > 0 && (
        <div className="mt-4 space-y-3 border-t border-[var(--pv-border)] pt-4">
          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]"><Clock className="h-3 w-3" /> Decided</p>
          {decided.map((r) => <Row key={r.id} r={r} />)}
        </div>
      )}
    </section>
  );
};
